// Shared case study data for the public site.
// Extend as needed — each item gets a detail page at /work/:slug.

export const CASE_STUDIES = [
  {
    slug: "rrl-builders-developers",
    client: "RRL Builders & Developers",
    industry: "Real Estate",
    tags: ["Performance Marketing", "Meta Ads", "Google Ads"],
    heroMetric: "300%",
    heroMetricLabel: "qualified site-visits in one quarter",
    summary: "Switched from broad-match display to tight geo-targeted search + broker retargeting. Cost per site-visit dropped 62%.",
    cover: "https://ik.imagekit.io/j0xzq9pns/RRl%20website%20banners%20(1536%20x%20752%20px)/RRl%20website%20banners%20(1536%20x%20752%20px)%20(19).png?updatedAt=1767177078002",
    gallery: [
      "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200",
      "https://ik.imagekit.io/j0xzq9pns/RRl%20website%20banners%20(1536%20x%20752%20px)/RRl%20website%20banners%20(1536%20x%20752%20px)%20(19).png?updatedAt=1767177078002",
    ],
    objective: "Re-launch a premium gated-community real estate project across Bengaluru with a sharper site-visit pipeline — replacing a previous agency's broad-match display spend.",
    challenge: "Unqualified site-visits were eating broker bandwidth; CPL was high and conversion rate at site was under 6%. Attribution was fragmented across 4 disconnected tools.",
    approach: [
      "Replaced broad display with locked-geo intent campaigns on Google Search + Meta",
      "Built broker retargeting audiences based on CRM stage data",
      "Relaunched landing pages with sub-2s LCP and embedded floor-plan modals",
      "Weekly creative rotation — 3 new ad variants shipped every Monday",
    ],
    results: [
      { k: "300%", v: "qualified site-visit uplift in 90 days" },
      { k: "-62%", v: "cost per site-visit vs. previous quarter" },
      { k: "4.6x", v: "ROAS sustained across the campaign" },
      { k: "18%", v: "walk-in → booking conversion at site" },
    ],
    testimonial: {
      quote: "They rebuilt the pipeline from the LP up. Site-visits are now our best-converting channel.",
      name: "Rohit Shah",
      title: "VP Marketing, RRL Builders & Developers",
    },
    ctaNext: "sunrise-designhive",
  },
  {
    slug: "sunrise-designhive",
    client: "Sunrise Designhive",
    industry: "Interior Design",
    tags: ["SEO", "GMB", "Content"],
    heroMetric: "#1",
    heroMetricLabel: "local rank in Bengaluru in 90 days",
    summary: "GMB overhaul + 30 reviews + content velocity. Now the top 3-pack result for 'interior design Bengaluru'.",
    cover: "https://www.designhive.in/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fbg.3b53d97e.png&w=1920&q=100",
    gallery: [
      "/images/marketly-interior-showcase.png",
    ],
    objective: "Own Bengaluru's 3-pack for 'interior design' without paid media — and turn GMB into a primary lead source.",
    challenge: "Brand ranked 9–11th locally. GMB profile was incomplete; only 7 reviews; zero posts; category mis-tagged.",
    approach: [
      "Full GMB profile audit + category correction + photo set of 120 images",
      "Review-generation workflow: post-project WhatsApp flow with direct review link",
      "Content velocity — 8 long-form guides per month targeting long-tail local queries",
      "Citations cleanup across 40 directories for NAP consistency",
    ],
    results: [
      { k: "#1", v: "Bengaluru 3-pack for primary keyword (90 days)" },
      { k: "+212%", v: "calls from GMB profile" },
      { k: "4.9★", v: "average rating, from 7 → 94 reviews" },
      { k: "+140%", v: "direction requests" },
    ],
    testimonial: {
      quote: "Marketly didn't sell us ads. They sold us a system that compounds every month.",
      name: "Pavan Kumar",
      title: "Director, Sunrise Designhive",
    },
    ctaNext: "craftroots",
  },
  {
    slug: "craftroots",
    client: "Craftroots",
    industry: "E-commerce",
    tags: ["SEO", "Programmatic", "Feed Optimization"],
    heroMetric: "+80%",
    heroMetricLabel: "organic traffic, 6× ROAS sustained",
    summary: "Programmatic category pages + fast product schema + feed optimisation. ROAS held through the quarter.",
    cover: "https://ik.imagekit.io/j0xzq9pns/image.png",
    gallery: [],
    objective: "Scale organic + paid without bloating CPC, across a 3,200-SKU D2C artisanal catalogue.",
    challenge: "Category pages were thin; product schema missing; Shopping feed ignored. Organic was flatlining for 4 months.",
    approach: [
      "Programmatic category pages (600+) with schema and unique copy blocks",
      "Shopping feed rebuilt — GTIN, condition, availability, price drops",
      "Core Web Vitals pass: LCP 3.8s → 1.4s on product pages",
      "Merchant Center issues cleared; Performance Max campaigns sharpened",
    ],
    results: [
      { k: "+80%", v: "organic sessions in 2 quarters" },
      { k: "6.1x", v: "sustained blended ROAS" },
      { k: "-34%", v: "cost per acquisition" },
      { k: "+52%", v: "AOV via cross-sell surfaces" },
    ],
    testimonial: {
      quote: "Our CMO sleeps better, not worse. Numbers move the right way every week.",
      name: "Ritika Desai",
      title: "Head of Growth, Craftroots",
    },
    ctaNext: "motobay",
  },
  {
    slug: "motobay",
    client: "Motobay",
    industry: "Automobile",
    tags: ["Meta Ads", "Creative Testing"],
    heroMetric: "+20%",
    heroMetricLabel: "leads, CPL down ₹412 → ₹238",
    summary: "Creative-first testing framework + interest-stack segmentation on Meta. Scaled while dropping blended CPL by 42%.",
    cover: "https://images.unsplash.com/photo-1556333720-fd197b661343?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
    gallery: [],
    objective: "Scale monthly lead volume 2x without increasing spend for a multi-location auto showroom chain.",
    challenge: "Stagnant creative fatigue; a single ad set had been running for 10 months. Cost-per-lead had climbed 34% YoY.",
    approach: [
      "Weekly creative-rotation framework: 5 new variants every Monday",
      "Interest-stack segmentation: 4 distinct audience pools + lookalikes",
      "Test → scale → kill loop with 72-hour performance gates",
      "Showroom visit tracking via offline conversions upload",
    ],
    results: [
      { k: "+20%", v: "lead volume, same budget" },
      { k: "-42%", v: "blended cost per lead" },
      { k: "3.1x", v: "showroom visit rate on qualified leads" },
    ],
    testimonial: {
      quote: "Honest dashboards. Honest reporting. When something didn't work, they told us first.",
      name: "Sunil Sharma",
      title: "CEO, Motobay",
    },
    ctaNext: "arina-co",
  },
  {
    slug: "arina-co",
    client: "Arina & Co.",
    industry: "Retail",
    tags: ["Meta Ads", "OOH", "GMB"],
    heroMetric: "+200%",
    heroMetricLabel: "foot-traffic, store opened with waitlist",
    summary: "Pre-launch lookalike audiences + opening-week GMB push + outdoor banners across three neighbourhoods.",
    cover: "https://images.unsplash.com/photo-1690489965043-ec15758cce71?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
    gallery: [],
    objective: "Launch a new flagship retail store in Indiranagar with a pre-opening waitlist and day-one footfall.",
    challenge: "Zero brand awareness in the local neighbourhood. Opening date fixed, 6-week runway only.",
    approach: [
      "Pre-launch Meta awareness campaign with neighbourhood-scoped lookalikes",
      "Outdoor: 4 hoardings + transit branding across 3 adjacent neighbourhoods",
      "GMB created pre-launch with teaser posts + opening-day push",
      "Influencer preview night with 30 micro-creators the week before opening",
    ],
    results: [
      { k: "+200%", v: "foot-traffic vs. projections, opening weekend" },
      { k: "1,200", v: "pre-opening waitlist signups" },
      { k: "38%", v: "walk-in → purchase conversion in week 1" },
    ],
    testimonial: {
      quote: "They shot the campaign once, then shipped it to Meta, Google, hoardings, and our window. One team. Zero friction.",
      name: "Aditya Rao",
      title: "Founder, Arina & Co.",
    },
    ctaNext: "rrl-builders-developers",
  },
];

export const getCaseStudy = (slug) => CASE_STUDIES.find((c) => c.slug === slug);
